
import * as React from 'react';
import type {
  DataPoint,
  AsinAnalysisResult,
  ReallocationPlan,
  ProfitImpact,
  Tab,
  VerificationResult,
  VerificationSummary,
  PortfolioHealthMetrics,
  // Analytics Extension Types
  ProductHierarchyMap,
  AggregationLevel,
  SalesDataPoint,
  PricingDataPoint,
  AdvertisingDataPoint,
  InventoryDataPoint,
  PromotionDataPoint,
  PLMetrics,
  YoYComparison,
  DemandForecast,
} from '@/shared/types';
import type { OptimizationProgress } from '@/workers/optimizationWorker.types';

// 1. State and Action interfaces
export interface AppState {
  activeTab: Tab;
  portfolioData: Map<string, DataPoint[]> | null;
  mostRecentPoints: Map<string, DataPoint> | null;
  fileName: string;
  marginFileName: string;
  defaultContributionMargin: number;
  asinMargins: Map<string, string>;
  marginSearchTerm: string;
  analysisResults: AsinAnalysisResult[] | null;
  analysisMargins: Map<string, number> | null;
  reallocationPlan: ReallocationPlan | null;
  profitImpact: ProfitImpact | null;
  portfolioHealthMetrics: PortfolioHealthMetrics | null;
  selectedAsin: AsinAnalysisResult | null;
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;

  // Analysis Progress State (for Web Worker)
  analysisProgress: OptimizationProgress | null;
  analysisWasCancelled: boolean;
  failedAsins: { asin: string; error: string }[] | null;

  // Verification State
  verificationData: Map<string, DataPoint> | null;
  verificationResults: VerificationResult[] | null;
  verificationSummary: VerificationSummary | null;
  isVerifying: boolean;
  verificationError: string | null;
  verificationFileName: string;

  // Analytics Extension: Product Hierarchy State
  productHierarchy: ProductHierarchyMap | null;
  selectedAggregationLevel: AggregationLevel;
  selectedAggregationId: string | null;

  // Analytics Extension: Time Series Data State
  salesData: Map<string, SalesDataPoint[]> | null;
  pricingData: Map<string, PricingDataPoint[]> | null;
  advertisingData: Map<string, AdvertisingDataPoint[]> | null;
  inventoryData: Map<string, InventoryDataPoint[]> | null;
  promotionData: Map<string, PromotionDataPoint[]> | null;

  // Analytics Extension: Computed Metrics State
  plMetrics: PLMetrics[] | null;
  yoyComparisons: YoYComparison[] | null;
  demandForecasts: DemandForecast[] | null;
}

export type AppAction =
  | { type: 'SET_ACTIVE_TAB'; payload: Tab }
  | { type: 'FILE_UPLOAD_START'; payload: { fileName: string } }
  | {
      type: 'FILE_UPLOAD_SUCCESS';
      payload: {
        portfolioData: Map<string, DataPoint[]>;
        mostRecentPoints: Map<string, DataPoint>;
        parsedMargins: Map<string, number> | null;
        successMessage: string;
      };
    }
  | { type: 'FILE_UPLOAD_ERROR'; payload: string }
  | { type: 'MARGIN_FILE_UPLOAD_START'; payload: { marginFileName: string } }
  | {
      type: 'MARGIN_FILE_UPLOAD_SUCCESS';
      payload: {
        newAsinMargins: Map<string, string>;
        successMessage: string;
      };
    }
  | { type: 'MARGIN_FILE_UPLOAD_ERROR'; payload: string }
  | { type: 'UPDATE_DEFAULT_MARGIN'; payload: number }
  | { type: 'UPDATE_ASIN_MARGIN'; payload: { asin: string; value: string } }
  | { type: 'UPDATE_BULK_ASIN_MARGINS'; payload: { asins: string[]; value: string } }
  | { type: 'UPDATE_MARGIN_SEARCH_TERM'; payload: string }
  | { type: 'RUN_ANALYSIS_START' }
  | { type: 'RUN_ANALYSIS_PROGRESS'; payload: OptimizationProgress }
  | {
      type: 'RUN_ANALYSIS_SUCCESS';
      payload: {
        results: AsinAnalysisResult[];
        plan: ReallocationPlan;
        impact: ProfitImpact;
        portfolioHealthMetrics: PortfolioHealthMetrics;
        finalMargins: Map<string, number>;
        failedAsins?: { asin: string; error: string }[];
      };
    }
  | { type: 'RUN_ANALYSIS_CANCELLED'; payload: { completedCount: number; totalCount: number } }
  | { type: 'RUN_ANALYSIS_ERROR'; payload: string }
  | { type: 'RESET_STATE' }
  | { type: 'SELECT_ASIN'; payload: AsinAnalysisResult | null }
  | { type: 'VERIFICATION_FILE_UPLOAD_START', payload: { fileName: string } }
  | { type: 'VERIFICATION_FILE_UPLOAD_SUCCESS', payload: { verificationData: Map<string, DataPoint>, results: VerificationResult[], summary: VerificationSummary } }
  | { type: 'VERIFICATION_FILE_UPLOAD_ERROR', payload: string }
  | {
      type: 'LOAD_SAVED_ANALYSIS';
      payload: {
        analysisResults: AsinAnalysisResult[];
        reallocationPlan: ReallocationPlan;
        profitImpact: ProfitImpact;
        mostRecentPoints: Map<string, DataPoint>;
        contributionMargins: Map<string, number>;
        analysisName: string;
      };
    }
  | {
      type: 'SET_PORTFOLIO_DATA';
      payload: {
        portfolioData: Map<string, DataPoint[]>;
        mostRecentPoints: Map<string, DataPoint>;
        fileName: string;
      };
    }
  // Analytics Extension Actions
  | { type: 'SET_PRODUCT_HIERARCHY'; payload: ProductHierarchyMap }
  | { type: 'SET_AGGREGATION_LEVEL'; payload: AggregationLevel }
  | { type: 'SET_AGGREGATION_ID'; payload: string | null }
  | { type: 'SET_SALES_DATA'; payload: Map<string, SalesDataPoint[]> }
  | { type: 'SET_PRICING_DATA'; payload: Map<string, PricingDataPoint[]> }
  | { type: 'SET_ADVERTISING_DATA'; payload: Map<string, AdvertisingDataPoint[]> }
  | { type: 'SET_INVENTORY_DATA'; payload: Map<string, InventoryDataPoint[]> }
  | { type: 'SET_PROMOTION_DATA'; payload: Map<string, PromotionDataPoint[]> }
  | { type: 'SET_PL_METRICS'; payload: PLMetrics[] }
  | { type: 'SET_YOY_COMPARISONS'; payload: YoYComparison[] }
  | { type: 'SET_DEMAND_FORECASTS'; payload: DemandForecast[] }
  | { type: 'CLEAR_ANALYTICS_DATA' };

// 2. Initial State
const initialState: AppState = {
  activeTab: 'optimizer',
  portfolioData: null,
  mostRecentPoints: null,
  fileName: '',
  marginFileName: '',
  defaultContributionMargin: 15,
  asinMargins: new Map(),
  marginSearchTerm: '',
  analysisResults: null,
  analysisMargins: null,
  reallocationPlan: null,
  profitImpact: null,
  portfolioHealthMetrics: null,
  selectedAsin: null,
  isLoading: false,
  error: null,
  successMessage: null,
  // Analysis Progress State
  analysisProgress: null,
  analysisWasCancelled: false,
  failedAsins: null,
  // Verification State
  verificationData: null,
  verificationResults: null,
  verificationSummary: null,
  isVerifying: false,
  verificationError: null,
  verificationFileName: '',

  // Analytics Extension: Product Hierarchy State
  productHierarchy: null,
  selectedAggregationLevel: 'sku',
  selectedAggregationId: null,

  // Analytics Extension: Time Series Data State
  salesData: null,
  pricingData: null,
  advertisingData: null,
  inventoryData: null,
  promotionData: null,

  // Analytics Extension: Computed Metrics State
  plMetrics: null,
  yoyComparisons: null,
  demandForecasts: null,
};

// 3. Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    case 'FILE_UPLOAD_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        successMessage: null,
        fileName: action.payload.fileName,
        marginFileName: '',
        asinMargins: new Map(),
      };
    case 'FILE_UPLOAD_SUCCESS': {
      const newAsinMargins = new Map<string, string>();
      if (action.payload.parsedMargins) {
        action.payload.parsedMargins.forEach((margin, asin) => {
          newAsinMargins.set(asin, String(margin));
        });
      }
      return {
        ...state,
        isLoading: false,
        portfolioData: action.payload.portfolioData,
        mostRecentPoints: action.payload.mostRecentPoints,
        successMessage: action.payload.successMessage,
        asinMargins: newAsinMargins,
        marginFileName: action.payload.parsedMargins ? `(from ${state.fileName})` : '',
      };
    }
    case 'FILE_UPLOAD_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        portfolioData: null,
        mostRecentPoints: null,
        fileName: '',
      };
    case 'MARGIN_FILE_UPLOAD_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        successMessage: null,
        marginFileName: action.payload.marginFileName,
      };
    case 'MARGIN_FILE_UPLOAD_SUCCESS':
      return {
        ...state,
        isLoading: false,
        asinMargins: action.payload.newAsinMargins,
        successMessage: action.payload.successMessage,
      };
    case 'MARGIN_FILE_UPLOAD_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        marginFileName: '',
      };
    case 'UPDATE_DEFAULT_MARGIN':
      return { ...state, defaultContributionMargin: action.payload };
    case 'UPDATE_ASIN_MARGIN': {
      const newMap = new Map(state.asinMargins);
      newMap.set(action.payload.asin, action.payload.value);
      return { ...state, asinMargins: newMap };
    }
    case 'UPDATE_BULK_ASIN_MARGINS': {
      const newMap = new Map(state.asinMargins);
      action.payload.asins.forEach(asin => {
        newMap.set(asin, action.payload.value);
      });
      return { ...state, asinMargins: newMap };
    }
    case 'UPDATE_MARGIN_SEARCH_TERM':
        return { ...state, marginSearchTerm: action.payload };
    case 'RUN_ANALYSIS_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        successMessage: null,
        analysisResults: null,
        reallocationPlan: null,
        analysisMargins: null,
        profitImpact: null,
        portfolioHealthMetrics: null,
        analysisProgress: null,
        analysisWasCancelled: false,
        failedAsins: null,
      };
    case 'RUN_ANALYSIS_PROGRESS':
      return {
        ...state,
        analysisProgress: action.payload,
      };
    case 'RUN_ANALYSIS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        analysisMargins: action.payload.finalMargins,
        analysisResults: action.payload.results,
        reallocationPlan: action.payload.plan,
        profitImpact: action.payload.impact,
        portfolioHealthMetrics: action.payload.portfolioHealthMetrics,
        analysisProgress: null,
        analysisWasCancelled: false,
        failedAsins: action.payload.failedAsins || null,
        // Reset verification state on new analysis
        verificationData: null,
        verificationResults: null,
        verificationSummary: null,
        isVerifying: false,
        verificationError: null,
        verificationFileName: '',
      };
    case 'RUN_ANALYSIS_CANCELLED':
      return {
        ...state,
        isLoading: false,
        analysisProgress: null,
        analysisWasCancelled: true,
        successMessage: `Analysis cancelled. Processed ${action.payload.completedCount} of ${action.payload.totalCount} ASINs.`,
      };
    case 'RUN_ANALYSIS_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        analysisProgress: null,
      };
    case 'RESET_STATE':
      return { ...initialState };
    case 'SELECT_ASIN':
      return { ...state, selectedAsin: action.payload };
    case 'VERIFICATION_FILE_UPLOAD_START':
        return {
          ...state,
          isVerifying: true,
          verificationError: null,
          verificationResults: null,
          verificationSummary: null,
          verificationFileName: action.payload.fileName,
        };
    case 'VERIFICATION_FILE_UPLOAD_SUCCESS':
        return {
          ...state,
          isVerifying: false,
          verificationData: action.payload.verificationData,
          verificationResults: action.payload.results,
          verificationSummary: action.payload.summary,
        };
    case 'VERIFICATION_FILE_UPLOAD_ERROR':
        return {
          ...state,
          isVerifying: false,
          verificationError: action.payload,
          verificationData: null,
          verificationResults: null,
          verificationSummary: null,
          verificationFileName: '',
        };
    case 'LOAD_SAVED_ANALYSIS': {
        // Convert contribution margins Map<string, number> to asinMargins Map<string, string>
        const loadedAsinMargins = new Map<string, string>();
        action.payload.contributionMargins.forEach((margin, asin) => {
          loadedAsinMargins.set(asin, String(margin));
        });
        return {
          ...state,
          // Set most recent points (needed for display)
          mostRecentPoints: action.payload.mostRecentPoints,
          // Set analysis results
          analysisResults: action.payload.analysisResults,
          analysisMargins: action.payload.contributionMargins,
          reallocationPlan: action.payload.reallocationPlan,
          profitImpact: action.payload.profitImpact,
          // Set asin margins for UI display
          asinMargins: loadedAsinMargins,
          // Update file name to show loaded analysis
          fileName: `Loaded: ${action.payload.analysisName}`,
          successMessage: `Loaded analysis: ${action.payload.analysisName}`,
          // Reset verification state
          verificationData: null,
          verificationResults: null,
          verificationSummary: null,
          isVerifying: false,
          verificationError: null,
          verificationFileName: '',
          // Clear any errors
          error: null,
          isLoading: false,
        };
    }
    case 'SET_PORTFOLIO_DATA': {
        return {
          ...state,
          portfolioData: action.payload.portfolioData,
          mostRecentPoints: action.payload.mostRecentPoints,
          fileName: action.payload.fileName,
          successMessage: `Loaded ${action.payload.portfolioData.size} ASINs from sample data.`,
          error: null,
          isLoading: false,
          // Reset analysis results when loading new data
          analysisResults: null,
          analysisMargins: null,
          reallocationPlan: null,
          profitImpact: null,
          portfolioHealthMetrics: null,
        };
    }

    // Analytics Extension Cases
    case 'SET_PRODUCT_HIERARCHY':
      return { ...state, productHierarchy: action.payload };
    case 'SET_AGGREGATION_LEVEL':
      return { ...state, selectedAggregationLevel: action.payload };
    case 'SET_AGGREGATION_ID':
      return { ...state, selectedAggregationId: action.payload };
    case 'SET_SALES_DATA':
      return { ...state, salesData: action.payload };
    case 'SET_PRICING_DATA':
      return { ...state, pricingData: action.payload };
    case 'SET_ADVERTISING_DATA':
      return { ...state, advertisingData: action.payload };
    case 'SET_INVENTORY_DATA':
      return { ...state, inventoryData: action.payload };
    case 'SET_PROMOTION_DATA':
      return { ...state, promotionData: action.payload };
    case 'SET_PL_METRICS':
      return { ...state, plMetrics: action.payload };
    case 'SET_YOY_COMPARISONS':
      return { ...state, yoyComparisons: action.payload };
    case 'SET_DEMAND_FORECASTS':
      return { ...state, demandForecasts: action.payload };
    case 'CLEAR_ANALYTICS_DATA':
      return {
        ...state,
        productHierarchy: null,
        selectedAggregationLevel: 'sku',
        selectedAggregationId: null,
        salesData: null,
        pricingData: null,
        advertisingData: null,
        inventoryData: null,
        promotionData: null,
        plMetrics: null,
        yoyComparisons: null,
        demandForecasts: null,
      };

    default:
      return state;
  }
}

// 4. Create Context and Provider
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = React.createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = React.useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// 5. Create custom hook
export const useAppContext = () => {
  const context = React.useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};